import React from 'react';
import { motion } from 'framer-motion';
import { UploadCloud, Check, X, ExternalLink, Trash2, BookOpen, User, Info, Calendar } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Card } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';

const ContributionsSection = ({
    contributions,
    handleContributionStatus,
    deleteDoc,
    doc,
    db,
    setContributions,
    toast,
    formatDate
}) => {
    return (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-8">
            <div className="relative p-10 rounded-[3rem] bg-violet-600 overflow-hidden shadow-2xl shadow-violet-500/20">
                <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl -mr-32 -mt-32 pointer-events-none" />
                <div className="absolute bottom-10 left-10 w-32 h-32 bg-white/5 rounded-full blur-2xl pointer-events-none" />

                <div className="relative z-10 flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
                    <div className="space-y-2">
                        <h2 className="text-3xl font-black text-white tracking-tight">Study Material Contributions</h2>
                        <p className="text-violet-100/80 text-sm font-bold uppercase tracking-widest">Review and manage student uploads for CodeCampus</p>
                    </div>
                    <div className="px-6 py-3 rounded-2xl bg-white/10 backdrop-blur-md border border-white/20 text-white font-black text-sm uppercase tracking-widest">
                        Pending: {contributions.filter(c => c.status === 'Pending').length}
                    </div>
                </div>
            </div>

            <Card className="bg-card/40 backdrop-blur-xl border-border/40 rounded-[2.5rem] overflow-hidden shadow-xl">
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead>
                            <tr className="bg-secondary/20 border-b border-border/40">
                                {['File Details', 'Contributor', 'Taxonomy', 'Status', 'Actions'].map(h => (
                                    <th key={h} className="px-8 py-5 text-left text-[10px] font-black uppercase tracking-widest text-muted-foreground opacity-70">{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-border/40">
                            {contributions.map((c, i) => (
                                <motion.tr
                                    key={c.id}
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: i * 0.05 }}
                                    className="group hover:bg-primary/[0.02] transition-colors"
                                >
                                    <td className="px-8 py-6">
                                        <div className="flex flex-col">
                                            <div className="font-black text-foreground tracking-tight group-hover:text-primary transition-colors">{c.fileName}</div>
                                            <div className="flex items-center gap-1.5 text-[10px] font-bold text-muted-foreground uppercase mt-1 opacity-60">
                                                <Calendar size={12} /> {formatDate(c.timestamp)}
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-8 py-6">
                                        <div className="flex items-center gap-3">
                                            <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center text-primary text-xs font-black">
                                                {c.userEmail ? c.userEmail[0].toUpperCase() : '?'}
                                            </div>
                                            <span className="text-xs font-bold text-foreground opacity-80">{c.userEmail}</span>
                                        </div>
                                    </td>
                                    <td className="px-8 py-6">
                                        <div className="space-y-1">
                                            <Badge variant="outline" className="rounded-lg border-primary/20 text-primary font-black text-[9px] uppercase tracking-widest px-2 py-0.5">
                                                {c.metadata?.subject}
                                            </Badge>
                                            <div className="text-[10px] font-bold text-muted-foreground px-1">
                                                Unit {c.metadata?.unit} • {c.metadata?.branch} • S{c.metadata?.semester}
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-8 py-6">
                                        <Badge className={`rounded-xl px-3 py-1 font-black text-[9px] uppercase tracking-widest ${c.status === 'Approved' ? 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20' :
                                            c.status === 'Rejected' ? 'bg-rose-500/10 text-rose-500 border-rose-500/20' :
                                                'bg-amber-500/10 text-amber-500 border-amber-500/20'
                                            }`}>
                                            {c.status || 'Pending'}
                                        </Badge>
                                    </td>
                                    <td className="px-8 py-6">
                                        <div className="flex gap-2">
                                            <Button
                                                asChild
                                                variant="ghost"
                                                size="icon"
                                                className="w-10 h-10 rounded-xl bg-primary/10 text-primary hover:bg-primary hover:text-white transition-all shadow-inner"
                                            >
                                                <a href={`https://drive.google.com/drive/folders/${process.env.REACT_APP_GOOGLE_DRIVE_FOLDER_ID || ''}?q=${encodeURIComponent(c.fileName)}`} target="_blank" rel="noopener noreferrer">
                                                    <ExternalLink size={18} />
                                                </a>
                                            </Button>

                                            {c.status === 'Pending' && (
                                                <>
                                                    <Button
                                                        onClick={() => handleContributionStatus(c, 'Approved')}
                                                        variant="ghost"
                                                        size="icon"
                                                        className="w-10 h-10 rounded-xl bg-emerald-500/10 text-emerald-500 hover:bg-emerald-500 hover:text-white transition-all shadow-inner"
                                                    >
                                                        <Check size={18} />
                                                    </Button>
                                                    <Button
                                                        onClick={() => handleContributionStatus(c, 'Rejected')}
                                                        variant="ghost"
                                                        size="icon"
                                                        className="w-10 h-10 rounded-xl bg-rose-500/10 text-rose-500 hover:bg-rose-500 hover:text-white transition-all shadow-inner"
                                                    >
                                                        <X size={18} />
                                                    </Button>
                                                </>
                                            )}

                                            <Button
                                                onClick={async () => {
                                                    if (window.confirm('Delete this record?')) {
                                                        try {
                                                            await deleteDoc(doc(db, 'contributions', c.id));
                                                            setContributions(prev => prev.filter(x => x.id !== c.id));
                                                            toast.success('Deleted');
                                                        } catch (err) {
                                                            toast.error('Failed to delete');
                                                        }
                                                    }
                                                }}
                                                variant="ghost"
                                                size="icon"
                                                className="w-10 h-10 rounded-xl bg-slate-500/10 text-slate-500 hover:bg-slate-500 hover:text-white transition-all shadow-inner"
                                            >
                                                <Trash2 size={18} />
                                            </Button>
                                        </div>
                                    </td>
                                </motion.tr>
                            ))}

                            {contributions.length === 0 && (
                                <tr>
                                    <td colSpan={5} className="p-24 text-center opacity-30">
                                        <UploadCloud size={64} className="mx-auto mb-6 opacity-20" />
                                        <div className="text-xl font-black text-foreground tracking-tight">Cloud is empty</div>
                                        <p className="text-sm font-bold text-muted-foreground uppercase tracking-widest mt-2">No submissions to review right now</p>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </Card>

            <Card className="p-6 bg-amber-500/5 border border-amber-500/20 rounded-[2rem] flex items-start gap-4">
                <div className="w-12 h-12 rounded-2xl bg-amber-500/10 flex items-center justify-center text-amber-500 flex-shrink-0">
                    <Info size={24} />
                </div>
                <div className="space-y-1">
                    <h4 className="font-black text-foreground text-sm tracking-tight uppercase">Admin Note</h4>
                    <p className="text-xs text-muted-foreground font-medium leading-relaxed">
                        Approved contributions automatically grant <span className="text-amber-600 font-black">50 Snap Points</span> to the contributor.
                        Ensure materials are high-quality and free of inappropriate content before approval.
                    </p>
                </div>
            </Card>
        </motion.div>
    );
};

export default ContributionsSection;
